import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { passwordChangeActions, passwordChangeSelectors } from '../reducer/password-change-reducer';
import { lionSelectors, LionTranslate } from '@rosetta/react-lion';
import styled from '@emotion/styled';
import { LaunchpadButton } from '../../../styles/LaunchpadButton';
import { appMainBackground, defaultBoxShadow, warningRedColor, white } from '../../../styles/colors';
import { InputWithIconContainer as InputWithIcon } from '../../app/util/InputWithIcon';
import greenCheck from '../../../assets/images/check-solid-green.svg';
import { MouseCatcherContainer as MouseCatcher } from '../../app/util/MouseCatcher';
import { appActions, appSelectors, TRANSACTION_STATE } from '../../app/reducer/app-reducer';
import { validateUserInfo } from '../../app/util/user-helper';
import { InputAndAlertContainerContainer as InputAndAlertContainer } from '../../app/util/InputAndAlertContainer';
import { withWindowDimensions } from '@rosetta/react-common-ui';

import { isMobile as isMobileDetected } from 'react-device-detect';

const ResetPasswordWrapper = styled.div((props) => {
  let style = {
    position: !props.isMobile ? 'absolute' : 'relative',
    top: '0px',
    bottom: '0px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: appMainBackground
  };
  return style;
});

const boxWidth = 400;
const boxSidePadding = 30;

const ResetPasswordHolder = styled.div((props) => {
  let style = {
    display: 'block',
    backgroundColor: !props.isMobile ? white : appMainBackground,
    width: '100%',
    maxWidth: boxWidth,
    paddingTop: '30px',
    paddingBottom: '30px',
    minHeight: '324px',
    boxShadow: !props.isMobile ? defaultBoxShadow : 'none',
    borderRadius: '4px'
  };
  return style;
});

const ResetPasswordHeading = styled.div((props) => {
  let style = {
    fontSize: !props.isMobile ? '30px' : '22px',
    fontWeight: '200',
    letterSpacing: '1px',
    lineHeight: '1.1',
    marginLeft: boxSidePadding,
    marginRight: boxSidePadding,
    textAlign: 'center'
  };

  return style;
});

const PasswordInputContainer = styled.div({
  fontSize: '14px',
  paddingTop: '30px',
  paddingBottom: '15px'
});

const ResetPasswordInstructions = styled.div((props) => {
  let style = {
    fontSize: !props.isMobile ? '18px' : '14px',
    fontWeight: '300',
    lineHeight: '22px',
    margin: '0 ' + boxSidePadding + 'px 20px',
    textAlign: !props.isMobile ? 'left' : 'center'
  };

  return style;
});

const SubmitButton = styled(LaunchpadButton)((props) => {
  let style = {
    padding: '0 25px',
    marginLeft: boxSidePadding,
    marginRight: boxSidePadding,
    width: 'auto'
  };
  if (props.isMobile) {
    style.borderRadius = '4px';
  }
  return style;
});

const resetBoxSidePadding = 30;
const ResetPasswordConfirmationHolder = styled.div((props) => {
  let style = {
    backgroundColor: !props.isMobile ? white : appMainBackground,
    padding: resetBoxSidePadding,
    width: boxWidth
  };

  return style;
});

const ResetPasswordConfirmationHeading = styled.div({
  fontSize: '30px',
  fontWeight: '150',
  letterSpacing: '1px',
  lineHeight: '1.1',
  margin: '0',
  paddingBottom: '20px',
  textAlign: 'center'
});

const ResetPasswordConfirmation = styled.div(() => {
  const iconSize = 36;
  let style = {
    backgroundImage: 'url(' + greenCheck + ')',
    backgroundRepeat: 'no-repeat',
    height: iconSize,
    width: iconSize,
    display: 'block',
    margin: 'auto'
  };
  return style;
});

const ResetPasswordConfirmationInstructions = styled.div({
  fontSize: '18px',
  fontWeight: '400',
  lineHeight: '22px',
  paddingTop: '20px'
});

const ValidationMessage = styled.div(() => {
  let style = {
    color: warningRedColor,
    fontSize: '12px',
    minHeight: '1px',
    marginBottom: 7,
    marginTop: 13,
    marginLeft: boxSidePadding,
    marginRight: boxSidePadding
  };
  return style;
});

const emailInputHeight = 34;

//*****************************************************************************
export class PasswordReset extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      password: '',
      passwordConfirm: '',
      validatingPassword: false,
      validatingPasswordMatch: false
    };

    // the following is probably only because of dev situations - if you get here by pasting
    //  the url in after failing at login or at password request, we need to clear the decks
    props.setLastTransactionState(TRANSACTION_STATE.NO_TRANSACTION);

    // ask ego what the rules are about creating passwords in this org
    props.loadPasswordMode({ userId: undefined, resetPasswordId: this.props.match.params.reset_uuid });
  }

  textInput = (event, firstPasswordPrompt) => {
    if (firstPasswordPrompt) {
      this.setState({
        password: event.target.value
      });
    } else {
      this.setState({
        passwordConfirm: event.target.value
      });
    }
  };

  mapEnterToAction = (event) => {
    if (event.key === 'Enter') {
      this.submitChange();
    }
  };

  passwordError = () => {
    return validateUserInfo('newPassword', this.state.password, '', this.props.passwordMode);
  };
  passwordMatchError = () => {
    return validateUserInfo(
      'newPasswordMatch',
      this.state.passwordConfirm,
      this.state.password,
      this.props.passwordMode
    );
  };

  isSubmitDisabled = () => {
    return (
      this.props.lastTransactionState !== TRANSACTION_STATE.NO_TRANSACTION ||
      this.passwordError().length > 0 ||
      this.passwordMatchError().length > 0
    );
  };

  submitChange = () => {
    if (!this.isSubmitDisabled()) {
      this.props.processPasswordReset(this.props.match.params.reset_uuid, this.state.password);
    }
  };

  // the password is only validated after the user clicks outside the input, the first time,
  //  and only if there is something in the field
  //  after the first time they type something in the match then blur, it always validates
  //  even if the field becomes empty - but not the first time if it is empty
  handlePasswordBlur = () => {
    if (this.state.password.length > 0) {
      this.setState({
        validatingPassword: true
      });
    }
  };

  // on this page the confirm password acts the same as the password, in terms of when to validate
  handlePasswordMatchBlur = () => {
    if (this.state.passwordConfirm.length > 0) {
      this.setState({
        validatingPasswordMatch: true
      });
    }
  };

  render = () => {
    if (!this.props.lionLoaded) {
      return <MouseCatcher />;
    }

    if (!this.props.passwordMode) {
      return <MouseCatcher />;
    }

    const isMobile = isMobileDetected;

    // this page shows success for a few seconds before whisking the user back to login
    if (this.props.lastTransactionState === TRANSACTION_STATE.SUCCESS) {
      return (
        <ResetPasswordWrapper isMobile={isMobile}>
          <ResetPasswordConfirmationHolder isMobile={isMobile}>
            <ResetPasswordConfirmationHeading>
              <LionTranslate lionkey="resetpassword_heading" />
            </ResetPasswordConfirmationHeading>
            <ResetPasswordConfirmation />
            <ResetPasswordConfirmationInstructions>
              <LionTranslate lionkey="profile_password_change_success" />
            </ResetPasswordConfirmationInstructions>
          </ResetPasswordConfirmationHolder>
        </ResetPasswordWrapper>
      );
    }

    const localizedPasswordPrompt = this.props.getLionTranslatedString('resetpassword_password_field');
    const localizedPasswordConfirmPrompt = this.props.getLionTranslatedString('resetpassword_repeatpassword_field');

    let validationMessageBlock = null;
    let passwordHasError = false;
    if (this.state.validatingPassword) {
      const passwordErrorKey = this.passwordError();
      if (passwordErrorKey.length > 0) {
        passwordHasError = true;
        validationMessageBlock = (
          <ValidationMessage data-qa="PasswordError">
            {this.props.getLionTranslatedString(passwordErrorKey)}
          </ValidationMessage>
        );
      }
    }

    let confirmationMessageBlock = null;
    const passwordMatchHasError = this.state.validatingPasswordMatch && this.passwordMatchError().length > 0;
    if (passwordMatchHasError) {
      confirmationMessageBlock = (
        <ValidationMessage data-qa="PasswordMatchError">
          {this.props.getLionTranslatedString(this.passwordMatchError())}
        </ValidationMessage>
      );
    }

    // handle the success icons now
    let successIconUrl = 'url(' + greenCheck + ')';
    const successIconSize = { width: 20, height: 20 };

    let passwordSuccessIconUrl = '';
    let passwordSuccessIconSize = { width: 0, height: 0 };
    if (this.state.validatingPassword && !passwordHasError) {
      passwordSuccessIconUrl = successIconUrl;
      passwordSuccessIconSize = successIconSize;
    }

    let passwordConfirmSuccessIconUrl = '';
    let passwordConfirmSuccessIconSize = { width: 0, height: 0 };
    if (this.state.validatingPasswordMatch && !passwordMatchHasError) {
      passwordConfirmSuccessIconUrl = successIconUrl;
      passwordConfirmSuccessIconSize = successIconSize;
    }

    //   show mousecatcher while request to reset is pending;
    //   continue showing mousecatcher on fail because we display error by
    //   going back to ForgotPassword and we should not redisplay here first
    let mouseCatcher = null;
    if (
      this.props.lastTransactionState === TRANSACTION_STATE.PENDING ||
      this.props.lastTransactionState === TRANSACTION_STATE.FAIL
    ) {
      mouseCatcher = <MouseCatcher />;
    }

    return (
      <ResetPasswordWrapper isMobile={isMobile}>
        <ResetPasswordHolder isMobile={isMobile}>
          <ResetPasswordHeading>
            <LionTranslate lionkey="resetpassword_heading" />
          </ResetPasswordHeading>
          <PasswordInputContainer>
            <ResetPasswordInstructions isMobile={isMobile}>
              <LionTranslate lionkey="resetpassword_instructions" />
            </ResetPasswordInstructions>
            {validationMessageBlock}
            <InputAndAlertContainer
              dataQa="NewPasswordContainer"
              displayError={passwordHasError}
              outsideHeight={emailInputHeight}
              marginBottom={10}
              inputSideMargin={boxSidePadding}
            >
              <InputWithIcon
                inputType="password"
                autoFocus={true}
                placeholder={localizedPasswordPrompt}
                tabIndex={1}
                value={this.state.password}
                onBlur={this.handlePasswordBlur}
                onChange={(event) => this.textInput(event, true)}
                onKeyPress={this.mapEnterToAction}
                outsideHeight={emailInputHeight}
                displayError={passwordHasError}
                specialIconUrl={passwordSuccessIconUrl}
                specialIconSize={passwordSuccessIconSize}
              />
            </InputAndAlertContainer>
            {confirmationMessageBlock}
            <InputAndAlertContainer
              dataQa="ConfirmPasswordContainer"
              displayError={passwordMatchHasError}
              outsideHeight={emailInputHeight}
              marginBottom={10}
              inputSideMargin={boxSidePadding}
            >
              <InputWithIcon
                inputType="password"
                placeholder={localizedPasswordConfirmPrompt}
                tabIndex={2}
                value={this.state.passwordConfirm}
                onBlur={this.handlePasswordMatchBlur}
                onChange={(event) => this.textInput(event, false)}
                onKeyPress={this.mapEnterToAction}
                outsideHeight={emailInputHeight}
                displayError={passwordMatchHasError}
                specialIconUrl={passwordConfirmSuccessIconUrl}
                specialIconSize={passwordConfirmSuccessIconSize}
              />
            </InputAndAlertContainer>
          </PasswordInputContainer>
          <SubmitButton
            tabIndex={3}
            onClick={this.submitChange}
            disabled={this.isSubmitDisabled()}
            onKeyPress={this.mapEnterToAction}
            isMobile={isMobile}
          >
            {this.props.getLionTranslatedString('resetpassword_button_changepassword')}
          </SubmitButton>
        </ResetPasswordHolder>
        {mouseCatcher}
      </ResetPasswordWrapper>
    );
  };
}

const mapStateToProps = (state, ownprops) => ({
  lionLoaded: lionSelectors.getLionAppResourcesLoaded(state),
  getLionTranslatedString: (key) => lionSelectors.getLionTranslatedString(state, key),
  isInterfaceRtl: appSelectors.isInterfaceRtl(state),
  lastTransactionState: appSelectors.lastTransactionState(state),
  passwordMode: passwordChangeSelectors.getPasswordMode(state)
});

const wrapActions = {
  setLastTransactionState: appActions.setLastTransactionState,
  loadPasswordMode: passwordChangeActions.loadPasswordMode,
  processPasswordReset: passwordChangeActions.processPasswordReset
};
export const PasswordResetContainer = connect(
  mapStateToProps,
  wrapActions
)(withWindowDimensions(PasswordReset));



// WEBPACK FOOTER //
// ./src/modules/login/components/PasswordReset.jsx